import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import { clearViewAs, getViewAs } from '../lib/view-as';
import { api } from '../redux/services/api';

export const AuthContext = createContext();

const parseJSON = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); }
  catch { return null; }
};

const parseUser = () => {
  const stored = parseJSON('user');
  // Invalidate old format (plain string username without _id)
  if (!stored || typeof stored !== 'object' || !stored._id) {
    localStorage.removeItem('user');
    return null;
  }
  return stored;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(parseUser);
  const [loggedIn, setLoggedIn] = useState(() => Boolean(parseUser()));
  const [activeCamp, setActiveCampState] = useState(() => parseJSON('activeCamp'));
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  // Con una vista previa activa, lo guardado en `permissions` son los permisos REALES
  // (`refreshIdentity` no los pisa a propósito). Sembrar el estado con ellos pintaría
  // la interfaz completa de administrador hasta que volviera `/auth/me`, que es justo
  // lo que la vista previa quiere evitar: se arranca vacío y manda el servidor.
  const [permissions, setPermissions] = useState(() => (getViewAs() ? [] : parseJSON('permissions') || []));
  // Permisos de la identidad REAL, sin la degradación de "Ver como".
  // Se necesitan aparte porque `permissions` refleja la vista previa: si el control
  // para salir de ella se pintara con esos, previsualizar un rol bajo lo escondería
  // y no habría forma de volver.
  const [realPermissions, setRealPermissions] = useState(() => parseJSON('permissions') || []);
  // ¿La lista de permisos es de fiar? Falso solo en sesiones abiertas ANTES de que el
  // login devolviera `permissions`, hasta que responda el primer `/auth/me`. Ver
  // `hasPermission`.
  const [permissionsLoaded, setPermissionsLoaded] = useState(() =>
    Array.isArray(parseJSON('permissions'))
  );
  // Vista previa según el SERVIDOR (`/auth/me` devuelve `viewAs` si la concedió), no
  // según lo que este navegador crea. Se siembra con el estado local para que la
  // primera pintada ya sepa que hay una vista previa en curso.
  const [viewAs, setViewAsState] = useState(getViewAs);
  const dispatch = useDispatch();

  useEffect(() => {
    const stored = parseUser();
    if (stored) {
      setUser(stored);
      setLoggedIn(true);
    }
  }, []);

  /**
   * Vuelve a preguntarle al servidor quién es este usuario.
   *
   * La petición sale por axios, cuyo interceptor añade las cabeceras `X-View-As-*`,
   * así que con la vista previa activa devuelve los permisos DEGRADADOS. Por eso hay
   * que llamarla también al entrar y salir de "Ver como": si no, la interfaz se
   * anuncia como "viendo como Juez" mientras sigue pintando el menú, los botones de
   * escritura y las rutas del administrador, y la vista previa no previsualiza nada
   * hasta que alguien recarga a mano.
   *
   * Un 401 aquí significa sesión caducada o revocada, y el interceptor de
   * `lib/http.js` ya se encarga de mandar al login; aquí solo se limpia el estado.
   */
  const refreshIdentity = useCallback(function pedirIdentidad(reintento = false) {
    if (!localStorage.getItem('token')) return Promise.resolve();

    return axios
      .get(`${process.env.REACT_APP_REST_API}/auth/me`)
      .then(({ data }) => {
        if (!data?.user) return;
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
        // La vista previa la confirma el SERVIDOR. Si mandamos cabeceras y no vuelve
        // `viewAs`, es que no la concedió y el estado local mentía.
        setViewAsState(data.viewAs || null);
        if (!data.viewAs && getViewAs()) clearViewAs();
        if (Array.isArray(data.permissions)) {
          setPermissions(data.permissions);
          setPermissionsLoaded(true);
          // Con vista previa activa, `data.permissions` son los del rol
          // previsualizado: no deben pisar los reales ni el almacenamiento.
          if (!data.viewAs) {
            setRealPermissions(data.permissions);
            localStorage.setItem('permissions', JSON.stringify(data.permissions));
          }
        }
      })
      .catch(err => {
        const status = err?.response?.status;
        if (status === 401) {
          logout();
          return undefined;
        }
        // 403 con vista previa activa: el servidor rechaza las cabeceras `X-View-As-*`
        // — típicamente porque quien las manda ya no tiene `users.change-role`. Es el
        // caso que dejaba la cuenta inservible: el banner y el conmutador para salir
        // se pintan con los permisos REALES, que aquí nunca llegan a cargarse, así que
        // no había forma de quitar las cabeceras sin vaciar localStorage a mano.
        // Se limpia la vista previa y se vuelve a preguntar como uno mismo.
        if (status === 403 && !reintento && getViewAs()) {
          clearViewAs();
          setViewAsState(null);
          return pedirIdentidad(true);
        }
        return undefined;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al montar se recontrasta la identidad con el servidor: si no, un rol o unos
  // permisos cambiados quedarían fosilizados en localStorage hasta el próximo login.
  useEffect(() => {
    refreshIdentity();
  }, [refreshIdentity]);

  const login = (userData, authToken, perms) => {
    // Antes que nada: la vista previa del usuario anterior no puede sobrevivir a un
    // cambio de cuenta. El login es público (sale sin cabeceras), así que se veía
    // bien; era la SIGUIENTE petición la que salía con `X-View-As-Role` ajeno y
    // devolvía 403 a alguien que no puede previsualizar nada.
    clearViewAs();
    setViewAsState(null);

    setUser(userData);
    setLoggedIn(true);
    localStorage.setItem('user', JSON.stringify(userData));
    // El backend calcula los permisos efectivos en el login. Aquí solo sirven para
    // pintar la interfaz: cada petición se vuelve a autorizar en el servidor.
    if (Array.isArray(perms)) {
      setPermissions(perms);
      setRealPermissions(perms);
      setPermissionsLoaded(true);
      localStorage.setItem('permissions', JSON.stringify(perms));
    }
    if (authToken) {
      setToken(authToken);
      localStorage.setItem('token', authToken);
    }
    // Al hacer login nuevo se limpia el camp para forzar selección
    setActiveCampState(null);
    localStorage.removeItem('activeCamp');
  };

  const logout = () => {
    setUser(null);
    setLoggedIn(false);
    setActiveCampState(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('activeCamp');
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    setPermissions([]);
    setRealPermissions([]);
    setPermissionsLoaded(false);
    // La vista previa NO puede sobrevivir al cierre de sesión: quedaba en
    // localStorage y el siguiente usuario del mismo navegador salía con cabeceras
    // `X-View-As-*` que no le corresponden. Su cuenta quedaba inutilizable.
    clearViewAs();
    setViewAsState(null);
    // Y la caché de RTK Query tampoco: sin esto, el siguiente usuario ve las
    // respuestas del anterior hasta que cada consulta se revalida.
    dispatch(api.util.resetApiState());
  };

  /**
   * Solo decide qué se pinta. La autorización real la hace el servidor en cada
   * petición: esto nunca es la única barrera.
   *
   * El fallback por rol NO puede depender de que la lista esté vacía. Una lista vacía
   * es un estado legítimo y frecuente: bajo "Ver como Juez" el servidor devuelve
   * `permissions: []` (la intersección con `Basic`), pero `user` sigue siendo el
   * registro real del administrador. Con la condición antigua (`permissions.length > 0`)
   * TODO `hasPermission()` devolvía `true`: se pintaba el menú, las rutas y los botones
   * de escritura del administrador mientras cada petición respondía 403, y la rama
   * `SinAcceso` no podía dispararse nunca. Lo mismo le pasaba a un admin con todos los
   * permisos en `deny`.
   *
   * Ahora el fallback solo cubre lo que decía cubrir: sesiones abiertas ANTES de que
   * el login devolviera `permissions`, mientras no haya respondido el primer
   * `/auth/me` y sin vista previa por medio.
   */
  const usarFallbackPorRol = !permissionsLoaded && !viewAs;
  const esAdminReal = user?.role === 'admin' || user?.role === 'super-admin';

  const hasPermission = useCallback(
    permission => (usarFallbackPorRol ? esAdminReal : permissions.includes(permission)),
    [usarFallbackPorRol, esAdminReal, permissions]
  );

  /** Como `hasPermission`, pero ignorando la vista previa. */
  const hasRealPermission = useCallback(
    permission => (usarFallbackPorRol ? esAdminReal : realPermissions.includes(permission)),
    [usarFallbackPorRol, esAdminReal, realPermissions]
  );

  /**
   * Zona que corresponde a la identidad EFECTIVA.
   *
   * Un administrador tiene `user.zone === null`, así que previsualizando
   * `sec · Zona 5` los filtros de la interfaz seguían mostrando "Todas" mientras el
   * servidor devolvía solo la Zona 5 — justo lo que la vista previa existe para
   * comprobar. La zona del servidor manda sobre la del usuario real.
   */
  const effectiveZone = viewAs?.zone ?? user?.zone ?? null;

  const setActiveCamp = (camp) => {
    setActiveCampState(camp);
    if (camp) {
      localStorage.setItem('activeCamp', JSON.stringify(camp));
    } else {
      localStorage.removeItem('activeCamp');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        loggedIn,
        user,
        token,
        login,
        logout,
        activeCamp,
        setActiveCamp,
        permissions,
        hasPermission,
        realPermissions,
        hasRealPermission,
        permissionsLoaded,
        viewAs,
        effectiveZone,
        refreshIdentity,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context)
    throw new Error(
      'UseAuthContext must be used within a Dashboard.Provider. Wrap a child component in the AuthProvider to fix this error',
    );

  return context;
};
